import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { secretsApi } from '@/lib/api/secrets';
import { SecretScope, SecretType, SecretMode } from '@optidev/shared-types';
import toast from 'react-hot-toast';
import {
  Key,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  Sparkles,
  Layers,
  FileText,
  FolderOpen,
  Building,
} from 'lucide-react';

interface AddSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  scope: SecretScope;
  projectId?: string;
}

export default function AddSecretModal({
  isOpen,
  onClose,
  onSuccess,
  scope,
  projectId,
}: AddSecretModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);

  const [key, setKey] = useState('');
  const [type, setType] = useState<SecretType>(SecretType.MASKED);
  const [mode, setMode] = useState<SecretMode>(SecretMode.LITE);
  const [baseValue, setBaseValue] = useState('');
  const [devValue, setDevValue] = useState('');
  const [stagingValue, setStagingValue] = useState('');
  const [prodValue, setProdValue] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!key.trim()) {
      toast.error('Key is required');
      return;
    }

    if (!baseValue.trim()) {
      toast.error('Base value is required');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const values =
        mode === SecretMode.LITE
          ? { base: baseValue }
          : {
              base: baseValue,
              development: devValue || baseValue,
              staging: stagingValue || baseValue,
              production: prodValue || baseValue,
            };

      await secretsApi.create(token, {
        scope,
        projectId,
        key: key.trim(),
        type,
        mode,
        values,
        description: description.trim() || undefined,
      });

      // Reset form
      setKey('');
      setBaseValue('');
      setDevValue('');
      setStagingValue('');
      setProdValue('');
      setDescription('');

      onSuccess();
    } catch (error: any) {
      console.error('Failed to create secret:', error);
      toast.error(error.response?.data?.message || 'Failed to create secret');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-lg">
              <Key className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            Add New Secret
          </DialogTitle>
          <DialogDescription>
            Create a new environment variable for your {scope === SecretScope.ACCOUNT ? 'account' : 'project'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Scope Badge */}
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium">Scope:</Label>
            <Badge
              variant={scope === SecretScope.ACCOUNT ? 'outline' : 'default'}
              className="gap-1.5"
            >
              {scope === SecretScope.ACCOUNT ? (
                <>
                  <Building className="h-3 w-3" />
                  Account Level
                </>
              ) : (
                <>
                  <FolderOpen className="h-3 w-3" />
                  Project Level
                </>
              )}
            </Badge>
          </div>

          {/* Security Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="security-type">Security Type</Label>
            <Select value={type} onValueChange={(value) => setType(value as SecretType)}>
              <SelectTrigger id="security-type" className="w-full">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-md ${
                    type === SecretType.VISIBLE ? 'bg-blue-100 dark:bg-blue-900/30' :
                    type === SecretType.MASKED ? 'bg-orange-100 dark:bg-orange-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    {type === SecretType.VISIBLE && <Eye className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />}
                    {type === SecretType.MASKED && <EyeOff className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />}
                    {type === SecretType.SENSITIVE && <Shield className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />}
                  </div>
                  <div className="flex items-center justify-between flex-1">
                    <span className="font-medium text-sm">
                      {type === SecretType.VISIBLE ? 'Visible' :
                       type === SecretType.MASKED ? 'Masked' : 'Sensitive'}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {type === SecretType.VISIBLE ? 'Plaintext' :
                       type === SecretType.MASKED ? 'Revealable' :
                       'Never shown'}
                    </span>
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SecretType.VISIBLE} className="py-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                      <Eye className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Visible</span>
                      <span className="text-xs text-muted-foreground">Plaintext, always visible</span>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value={SecretType.MASKED} className="py-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-orange-100 dark:bg-orange-900/30 rounded">
                      <EyeOff className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Masked</span>
                      <span className="text-xs text-muted-foreground">Encrypted, can be revealed</span>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value={SecretType.SENSITIVE} className="py-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-red-100 dark:bg-red-900/30 rounded">
                      <Shield className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Sensitive</span>
                      <span className="text-xs text-muted-foreground">Encrypted, never revealed</span>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Environment Mode */}
          <div className="space-y-2">
            <Label>Environment Mode</Label>
            <Tabs value={mode} onValueChange={(value) => setMode(value as SecretMode)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value={SecretMode.LITE} className="gap-2">
                  <Sparkles className="h-4 w-4" />
                  Lite Mode
                </TabsTrigger>
                <TabsTrigger value={SecretMode.POWER} className="gap-2">
                  <Layers className="h-4 w-4" />
                  Power Mode
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-xs text-muted-foreground">
              {mode === SecretMode.LITE
                ? 'Single value used across all environments'
                : 'Different values for Development, Staging, and Production'}
            </p>
          </div>

          {/* Secret Key */}
          <div className="space-y-2">
            <Label htmlFor="key" className="flex items-center gap-2">
              Key
              <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="key"
                placeholder="e.g., STRIPE_API_KEY, DATABASE_URL"
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
                className="pl-10 font-mono"
                maxLength={255}
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Use UPPERCASE with underscores. Special characters will be converted.
            </p>
          </div>

          {/* Secret Values */}
          <div className="space-y-4">
            {mode === SecretMode.LITE ? (
              <div className="space-y-2">
                <Label htmlFor="baseValue" className="flex items-center gap-2">
                  Value
                  <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="baseValue"
                  placeholder={type === SecretType.VISIBLE
                    ? "Enter the secret value"
                    : "Enter the secret value (will be encrypted)"}
                  value={baseValue}
                  onChange={(e) => setBaseValue(e.target.value)}
                  rows={3}
                  className="font-mono text-sm"
                  required
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="baseValue" className="flex items-center gap-2">
                    Value
                    <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="baseValue"
                    placeholder="Default value for all environments"
                    value={baseValue}
                    onChange={(e) => setBaseValue(e.target.value)}
                    rows={2}
                    className="font-mono text-sm"
                    required
                  />
                </div>

                <div className="grid gap-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="devValue" className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                      Development
                    </Label>
                    <Input
                      id="devValue"
                      placeholder="Leave empty to use base value"
                      value={devValue}
                      onChange={(e) => setDevValue(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stagingValue" className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      Staging
                    </Label>
                    <Input
                      id="stagingValue"
                      placeholder="Leave empty to use base value"
                      value={stagingValue}
                      onChange={(e) => setStagingValue(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prodValue" className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      Production
                    </Label>
                    <Input
                      id="prodValue"
                      placeholder="Leave empty to use base value"
                      value={prodValue}
                      onChange={(e) => setProdValue(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description
              <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Add a helpful description for your team..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/1000 characters
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Create Secret
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
